import React from 'react';
import { useLocation } from 'react-router-dom';
import { hasAccountRole } from '@/lib/account-roles';
import { useSessionQuery } from '@/queries/auth/useSessionQuery';
import {
  useActiveOrgQuery,
  useAllOrgsQuery,
  useImpersonateOrgMutation,
} from '@/queries/org';
import { useAuthStore } from '@/stores/auth-store';

export default function OrgSwitcher() {
  const location = useLocation();
  const pathname = (location.pathname ?? '/').replace(/\/$/, '') || '/';
  const isMainScreen = pathname === '/';

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);
  const isRoot = hasAccountRole(currentUser, 'ROOT');
  const { isSuccess: sessionVerified } = useSessionQuery();
  const queryEnabled = isRoot && isAuthenticated && sessionVerified;

  const {
    data: activeOrg,
    isPending: activeOrgLoading,
    isError: activeOrgError,
  } = useActiveOrgQuery({
    enabled: queryEnabled,
  });
  const {
    data: allOrgs = [],
    isPending: orgsLoading,
    isError: allOrgsError,
  } = useAllOrgsQuery({
    enabled: queryEnabled,
  });
  const impersonate = useImpersonateOrgMutation();

  if (!isRoot) {
    return null;
  }

  const orgSwitchingUnavailable = activeOrgError || allOrgsError;
  const activeOrgId = activeOrg?.orgId;
  const loading =
    (queryEnabled && (activeOrgLoading || orgsLoading)) ||
    impersonate.isPending;
  const controlsDisabled =
    loading || orgSwitchingUnavailable || activeOrgId == null || !isMainScreen;
  const showReset =
    !orgSwitchingUnavailable && activeOrg?.impersonated === true;

  const handleChange = (event) => {
    const orgId = Number(event.target.value);
    if (!Number.isFinite(orgId) || orgId === activeOrgId) {
      return;
    }
    impersonate.mutate(orgId);
  };

  const handleReset = () => {
    impersonate.mutate(0);
  };

  const emptyLabel = (() => {
    if (loading) {
      return 'Loading organizations…';
    }
    if (orgSwitchingUnavailable) {
      return 'Organization switching unavailable';
    }
    return 'No organization';
  })();

  return (
    <div className="bethere-org-switcher">
      <select
        id="org-switcher"
        className="bethere-org-switcher-select"
        value={activeOrgId != null ? String(activeOrgId) : ''}
        disabled={controlsDisabled}
        onChange={handleChange}
        aria-label="Switch organization"
      >
        {(activeOrgId == null || orgSwitchingUnavailable) && (
          <option value="" disabled>
            {emptyLabel}
          </option>
        )}
        {!orgSwitchingUnavailable &&
          allOrgs.map((org) => (
            <option key={org.orgId} value={String(org.orgId)}>
              {org.orgName}
            </option>
          ))}
      </select>
      {showReset && (
        <button
          type="button"
          className="bethere-org-switcher-reset-btn"
          disabled={controlsDisabled}
          onClick={handleReset}
        >
          Reset
        </button>
      )}
    </div>
  );
}
